/** @typedef {import('./index').GroupStanding} GroupStanding */
/** @typedef {import('./index').GroupRanking} GroupRanking */

/**
 * Extracts the group-stage standings from an Opta `matches` payload.
 *
 * Opta delivers tournament standings nested under
 * `standings.stage[0].division` and includes multiple "views" of each
 * division (`total`, `home`, `away`, etc.). This helper:
 *
 *   1. Validates the expected shape and bails out safely with `[]` if any
 *      piece is missing (so callers never need to null-check the result).
 *   2. Keeps only the `'total'` view of group-stage divisions
 *      (`groupName` beginning with `'Group '` — i.e. ignores knockout
 *      divisions, which use the same schema).
 *   3. Sorts the resulting groups by their `horizontal` coordinate so the
 *      UI renders them in the same left-to-right order Opta intends.
 *
 * The function is pure: it does not mutate `matches`.
 *
 * Memoised (issue #33): `GroupsPage`, `GroupTable` and `getMatches` (in
 * `./matches.js`) all consume this, sometimes on the same poller tick.
 * Caching by the input reference via `WeakMap` collapses those repeated
 * calls into a single computation per tick. The cache auto-GCs when the
 * input `matches` reference is replaced.
 *
 * @param {{
 *   standings?: {
 *     stage?: Array<{
 *       division?: GroupStanding[];
 *     }>;
 *   };
 * } | null | undefined} matches
 *   The matches payload (typically `data.matches`).
 * @returns {GroupStanding[]}
 *   The group-stage standings, sorted by `horizontal`. Returns an empty
 *   array when no standings are available.
 */

const cache = new WeakMap();

const buildGroups = (matches) => {
  const divisions = matches?.standings?.stage?.[0]?.division;
  if (!Array.isArray(divisions)) return [];

  const groupStandings = divisions.filter(
    (d) => d?.type === 'total' && d?.groupName?.startsWith('Group ')
  );

  // Sort by Opta's `horizontal` coordinate so groups render in the same
  // left-to-right order across the UI. Fall back to `0` so missing values
  // don't push entries to NaN positions.
  return groupStandings.sort(
    (a, b) => (a.horizontal ?? 0) - (b.horizontal ?? 0)
  );
};

export default (matches) => {
  if (!matches || typeof matches !== 'object') return buildGroups(matches);
  const hit = cache.get(matches);
  if (hit) return hit;
  const built = buildGroups(matches);
  cache.set(matches, built);
  return built;
};
